import React, { useEffect, useMemo, useState } from 'react';
import { gameLogger } from '../../../../utils/GameLogger';
import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import type { ItemEquipmentSlot } from '../../../../shared/equipmentSlots';
import { useGame } from '../../contexts/GameContext';
import { CharacterPortrait } from '../../components/CharacterPortrait';
import { Button } from '../../primitives/Button';
import { useEquipment } from '../../hooks/useEquipment';
import { useInventory } from '../../hooks/useInventory';
import { formatCallingWithArchetype } from '../../theme';
import { CharacterEquipmentSlot } from './CharacterEquipmentSlot';
import {
  EQUIPMENT_SLOTS, compatibleInventoryItems, slotDefinition, itemEquipmentSlot,
  readDraggedItemId, dropToast, rarityTone, humanize, formatItemStat,
  type EquipmentSlotDefinition, type EquipmentDropState, type DraggedInventoryItem,
} from './characterPresentation';

export const EquipmentContent: React.FC = () => {
  const { player } = useGame();
  const { slots, equip, unequip } = useEquipment();
  const { items: inventoryItems } = useInventory();
  const [selectedSlot, setSelectedSlot] = useState<ItemEquipmentSlot>('weapon');
  const [draggedItem, setDraggedItem] = useState<DraggedInventoryItem | null>(null);
  const [dropTarget, setDropTarget] = useState<ItemEquipmentSlot | 'character' | null>(null);

  const selectedItem = slots[selectedSlot] ?? null;
  const selectedCanonicalItem = selectedItem ? ITEM_DATABASE[selectedItem.id] : undefined;
  const selectedDefinition = slotDefinition(selectedSlot);
  const compatibleItems = useMemo(
    () => compatibleInventoryItems(inventoryItems, selectedSlot),
    [inventoryItems, selectedSlot],
  );
  const previewReady = player?.appearance != null && player.equipmentVisuals != null;

  useEffect(() => {
    const handleGlobalDragStart = (event: Event) => {
      const detail = (event as CustomEvent<{ type?: string; itemId?: string }>).detail;
      if (detail?.type !== 'item' || !detail.itemId) {
        setDraggedItem(null);
        return;
      }
      const item = ITEM_DATABASE[detail.itemId];
      setDraggedItem({
        itemId: detail.itemId,
        name: item?.name ?? detail.itemId,
        equipmentSlot: itemEquipmentSlot(detail.itemId),
      });
    };
    const handleGlobalDragEnd = () => {
      setDraggedItem(null);
      setDropTarget(null);
    };

    window.addEventListener('helix:action-drag-start', handleGlobalDragStart);
    window.addEventListener('dragend', handleGlobalDragEnd);
    return () => {
      window.removeEventListener('helix:action-drag-start', handleGlobalDragStart);
      window.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, []);

  const resetDragState = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  const equipRequestedItem = (itemId: string, expectedSlot?: ItemEquipmentSlot) => {
    const item = ITEM_DATABASE[itemId];
    const actualSlot = itemEquipmentSlot(itemId);
    if (!item || !actualSlot) {
      dropToast('Only equippable gear can go on the character screen');
      resetDragState();
      return;
    }
    if (expectedSlot && actualSlot !== expectedSlot) {
      dropToast(`${item.name} belongs in the ${slotDefinition(actualSlot).label.toLowerCase()} slot`);
      resetDragState();
      return;
    }

    gameLogger.debug('ui', `[Equipment] Requesting equip for ${itemId} in ${actualSlot}`);
    equip(itemId);
    resetDragState();
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLElement>,
    target: ItemEquipmentSlot | 'character',
  ) => {
    if (!draggedItem) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = draggedItem.equipmentSlot ? 'copy' : 'none';
    setDropTarget(target);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    const related = event.relatedTarget;
    if (!(related instanceof Node) || !event.currentTarget.contains(related)) {
      setDropTarget(null);
    }
  };

  const handleDrop = (
    event: React.DragEvent<HTMLElement>,
    expectedSlot?: ItemEquipmentSlot,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('helix:action-drag-end'));
    }
    const itemId = readDraggedItemId(event.dataTransfer) ?? draggedItem?.itemId ?? null;
    if (!itemId) {
      resetDragState();
      return;
    }
    equipRequestedItem(itemId, expectedSlot);
  };

  const requestUnequip = () => {
    if (!selectedItem) return;
    gameLogger.debug('ui', `[Equipment] Requesting unequip for ${selectedSlot}`);
    unequip(selectedSlot);
  };

  const selectSlot = (slotId: ItemEquipmentSlot) => {
    setSelectedSlot(slotId);
  };

  const characterDropState: EquipmentDropState = !draggedItem
    ? 'idle'
    : !draggedItem.equipmentSlot
      ? 'invalid'
      : dropTarget === 'character'
        ? 'valid'
        : 'available';

  const equipmentSlotDropState = (slotId: ItemEquipmentSlot): EquipmentDropState => {
    if (!draggedItem) return 'idle';
    if (draggedItem.equipmentSlot !== slotId) return 'invalid';
    return dropTarget === slotId ? 'valid' : 'available';
  };

  const itemStats = Object.entries(selectedCanonicalItem?.stats ?? selectedItem?.stats ?? {})
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number');
  const requirementLevel = selectedCanonicalItem?.requiredLevel;
  const requirementClass = selectedCanonicalItem?.requiredClass;

  const renderSlot = (definition: EquipmentSlotDefinition) => (
    <CharacterEquipmentSlot
      key={definition.id}
      definition={definition}
      item={slots[definition.id] ?? null}
      selected={selectedSlot === definition.id}
      dropState={equipmentSlotDropState(definition.id)}
      onSelect={() => selectSlot(definition.id)}
      onDragEnter={event => handleDragOver(event, definition.id)}
      onDragOver={event => handleDragOver(event, definition.id)}
      onDragLeave={handleDragLeave}
      onDrop={event => handleDrop(event, definition.id)}
    />
  );

  return (
    <section className="character-equipment" aria-labelledby="character-equipment-heading">
      <div
        className="character-equipment__scroll"
        data-character-scroll-owner="equipment"
        tabIndex={0}
        aria-label="Equipment sheet"
      >
        <header className="character-section-intro">
          <div>
            <p className="character-eyebrow">Loadout dossier</p>
            <h2 id="character-equipment-heading">Equipment</h2>
          </div>
          <p>Choose a slot to inspect it. Equipment changes appear only after server confirmation.</p>
        </header>

        <div className="character-equipment__layout">
          <div className="character-equipment__slots character-equipment__slots--left">
            {EQUIPMENT_SLOTS.filter(slot => slot.side === 'left').map(renderSlot)}
          </div>

          <section
            className="character-preview"
            aria-label="Live character preview and general equipment drop target"
            data-drop-state={characterDropState}
            onDragEnter={event => handleDragOver(event, 'character')}
            onDragOver={event => handleDragOver(event, 'character')}
            onDragLeave={handleDragLeave}
            onDrop={event => handleDrop(event)}
          >
            <div className="character-preview__stage">
              {previewReady ? (
                <CharacterPortrait
                  appearance={player.appearance!}
                  characterClass={player.class}
                  label={`${player.name}, current appearance and equipped gear`}
                  equipmentVisuals={player.equipmentVisuals!}
                  size={220}
                />
              ) : (
                <div className="character-preview__unavailable" role="status">
                  <strong>Preview unavailable</strong>
                  <span>Waiting for authoritative appearance and equipment visuals.</span>
                </div>
              )}
            </div>
            <div className="character-preview__drop-copy">
              {characterDropState === 'valid'
                ? `Release ${draggedItem?.name ?? 'gear'} to request equip`
                : characterDropState === 'invalid'
                  ? 'This item is not equippable gear'
                  : 'Drop compatible gear here, or use the selected-slot action below'}
            </div>
          </section>

          <div className="character-equipment__slots character-equipment__slots--right">
            {EQUIPMENT_SLOTS.filter(slot => slot.side === 'right').map(renderSlot)}
          </div>

          <div className="character-equipment__slots character-equipment__slots--center">
            {EQUIPMENT_SLOTS.filter(slot => slot.side === 'center').map(renderSlot)}
          </div>
        </div>

        <section className="character-item-detail" aria-labelledby="character-selected-slot-heading">
          <div className="character-item-detail__summary">
            <p className="character-eyebrow">{selectedDefinition.label} slot</p>
            <h3 id="character-selected-slot-heading">{selectedItem?.name ?? 'Empty slot'}</h3>
            {selectedItem ? (
              <>
                <span
                  className="character-item-detail__rarity"
                  style={{ color: rarityTone(selectedItem) }}
                >
                  {humanize(selectedCanonicalItem?.rarity ?? selectedItem.rarity ?? 'rarity unavailable')}
                </span>
                <p>{selectedCanonicalItem?.description ?? selectedItem.description ?? 'No description available.'}</p>
              </>
            ) : (
              <p>No item is equipped here. Choose compatible gear from the authoritative inventory list.</p>
            )}
          </div>

          <div className="character-item-detail__facts">
            <h4>Item facts</h4>
            {selectedItem ? (
              <>
                <div className="character-item-detail__fact-grid">
                  {itemStats.length > 0 ? itemStats.map(([stat, value]) => (
                    <div key={stat}>
                      <span>{humanize(stat)}</span>
                      <strong>{formatItemStat(value)}</strong>
                    </div>
                  )) : <p>No numeric item bonuses.</p>}
                </div>
                <ul className="character-item-detail__requirements" aria-label="Item requirements">
                  {requirementLevel && requirementLevel > 1 ? (
                    <li data-requirement-status={player && player.level >= requirementLevel ? 'met' : 'unmet'}>
                      Requires level {requirementLevel}
                    </li>
                  ) : null}
                  {requirementClass ? (
                    <li data-requirement-status={player?.class === requirementClass ? 'met' : 'unmet'}>
                      Requires {formatCallingWithArchetype(requirementClass)} class
                    </li>
                  ) : null}
                  {selectedCanonicalItem
                    && (!requirementLevel || requirementLevel <= 1)
                    && !requirementClass ? (
                      <li>No special requirements</li>
                    ) : null}
                  {!selectedCanonicalItem ? <li>Requirements unavailable</li> : null}
                </ul>
              </>
            ) : <p>Select inventory gear below to request an equip.</p>}
          </div>

          <div className="character-item-detail__actions">
            <h4>Compatible inventory gear</h4>
            <div className="character-compatible-items">
              {compatibleItems.length > 0 ? compatibleItems.map((item, index) => (
                <Button
                  key={`${item.id}-${index}`}
                  variant="outline"
                  size="sm"
                  aria-label={`Equip ${item.name} in ${selectedDefinition.label} slot`}
                  onClick={() => equipRequestedItem(item.id, selectedSlot)}
                >
                  Equip {item.name}
                </Button>
              )) : <p>No compatible gear in your bag.</p>}
            </div>
            <div className="character-item-detail__action-row">
              <Button
                variant="danger"
                size="sm"
                disabled={!selectedItem}
                onClick={requestUnequip}
              >
                {selectedItem ? `Unequip ${selectedItem.name}` : 'Nothing to unequip'}
              </Button>
            </div>
            <p className="character-item-detail__authority-note">
              Your gear updates after the realm confirms the change.
            </p>
          </div>
        </section>
      </div>
    </section>
  );
};
